/**
 * The one light the 3D view shades by, at either quality: a broad ambient
 * fill and one key light fixed in view space, so the light stays locked to
 * the camera and the model turns under it. There is no hemisphere term and
 * no shadow. High dims the ambient term by its ambient occlusion; Low takes
 * it whole.
 *
 * Both renderers declare the light with `SCENE_LIGHT_GLSL` and shade every
 * fill through its `sceneLight`, and both set its uniforms with
 * `applySceneLight`, so the two qualities can never drift apart. The view
 * cube shades its faces with `sceneLightAt`, so the cube reads like the house
 * beside it.
 */

import { dot3d, type Vec3 } from '../math3d';

const DEGREES = Math.PI / 180;

/**
 * A view-space direction from two offsets from the viewer: (0, 0) points
 * straight back toward the eye, azimuth swings it right, elevation lifts it.
 */
function viewSpaceDirection(azimuthDegrees: number, elevationDegrees: number): Vec3 {
  const azimuth = azimuthDegrees * DEGREES, elevation = elevationDegrees * DEGREES;
  return [Math.sin(azimuth) * Math.cos(elevation), Math.sin(elevation), Math.cos(azimuth) * Math.cos(elevation)];
}

/** The scene light, tuned by eye on device. */
export interface SceneLighting {
  /** The broad fill every surface takes, 0..1. */
  ambient: number;
  /** The key light's strength, 0..1. */
  direct: number;
  /** Toward the key light, in view space (x right, y up, z toward the viewer), unit length. */
  direction: Vec3;
}

/**
 * A gentle over-the-shoulder key light: 55° right of the viewer and 9° below,
 * kept low so the floor and the ceiling reshade clearly as the view tilts.
 */
export const SCENE_LIGHTING: SceneLighting = {
  ambient: 0.66,
  direct: 0.43,
  direction: viewSpaceDirection(55, -9),
};

/**
 * The light model in GLSL, the same text for GLSL ES 1.00 (Low) and 3.00
 * (High): the ambient fill, dimmed by `ambientOcclusion` (1 for none), plus
 * the clamped Lambert key light. Each flat face takes one brightness from its
 * view-space normal.
 */
export const SCENE_LIGHT_GLSL = `
uniform vec3 uLightDir;
uniform float uAmbient;
uniform float uDirect;
float sceneLight(vec3 viewNormal, float ambientOcclusion) {
  return uAmbient * ambientOcclusion + uDirect * max(dot(viewNormal, uLightDir), 0.0);
}`;

/** Set the scene light's uniforms on `program`, once, after it links: they never change. Leaves `program` in use. */
export function applySceneLight(gl: WebGLRenderingContext, program: WebGLProgram): void {
  const [x, y, z] = SCENE_LIGHTING.direction;
  gl.useProgram(program);
  gl.uniform3f(gl.getUniformLocation(program, 'uLightDir'), x, y, z);
  gl.uniform1f(gl.getUniformLocation(program, 'uAmbient'), SCENE_LIGHTING.ambient);
  gl.uniform1f(gl.getUniformLocation(program, 'uDirect'), SCENE_LIGHTING.direct);
}

/** The brightness `sceneLight` gives a surface with this view-space normal and no ambient occlusion, clamped to 1 as the screen clamps it. */
export const sceneLightAt = (viewNormal: Vec3): number =>
  Math.min(1, SCENE_LIGHTING.ambient + SCENE_LIGHTING.direct * Math.max(0, dot3d(viewNormal, SCENE_LIGHTING.direction)));
